<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="subject.isDiff"
    :isOutdated="isOutdated"
    :itemType="'SUBJECT'"
    :typename="'subject'"
    :item="subject"
    :onUpdate="updateSubject"
    :onCreate="createSubject"
    :onDelete="deleteSubject"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
    :isEdited="isSubjectEdited"
  >
    <fieldset slot="fieldset" :disabled="isOutdated">
      <div
        class="field required"
        :class="{ error: $v.subject.name.$error }"
        v-diff-field="subjectDiff.name"
      >
        <label for="subjectName">{{ $t('NAME') }}</label>
        <input
          id="subjectName"
          type="text"
          :placeholder="$t('NAME')"
          v-model.trim="subject.name"
          @click="$v.subject.name.$touch()"
          :disabled="subject.isDiff"
        />
      </div>
      <div
        class="field required"
        :class="{ error: $v.subject.key.$error }"
        v-diff-field="subjectDiff.key"
      >
        <label for="subjectKey">{{ $t('KEY') }}</label>
        <input
          id="subjectKey"
          type="text"
          :placeholder="$t('KEY')"
          v-model.trim="subject.key"
          @click="$v.subject.key.$touch()"
          :disabled="subject.isDiff"
        />
      </div>
      <regex-field
        v-model.trim="subject.regex"
        :labelText="$t('REGEX')"
        :disabled="subject.isDiff"
        :diff="subjectDiff.regex"
        class="required"
        :class="{ error: $v.subject.regex.$error }"
        @click="$v.subject.regex.$touch()"
      />
      <div class="field" :class="{ error: $v.editedGroups.$error }">
        <group-typeahead
          :allGroups="groups"
          :currentGroups="editedGroups"
          @selected="groupSelected"
          @created="groupCreated"
          @removed="groupRemoved"
          :disabled="subject.isDiff || !groups"
        />
      </div>
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import { Flow, Subject, Toast, Validation, ErrorCodes, UnsavedChecker, Group } from 'services'
import { MainSection, RegexField, GroupTypeahead } from 'ui'
import { take } from 'rxjs/operators'
import { required, minLength } from 'vuelidate/lib/validators'
import GroupAffiliated from '../Mixins/GroupAffiliated'

const subject = {
  name: '',
  key: '',
  regex: ''
}

export default {
  name: 'subject',
  mixins: [UnsavedChecker.mixin, GroupAffiliated],
  components: { MainSection, RegexField, GroupTypeahead },
  props: ['subjectId'],
  data() {
    return {
      mode: '',
      subject: { ...subject },
      isOutdated: false,
      subjectDiff: {}
    }
  },

  computed: {
    isSubjectEdited() {
      return this.areGroupsEdited
    },
    savedGroups() {
      return _.filter(this.groups, g => _.includes(g.subject, this.subject.id))
    },
    isEditMode() {
      return this.mode === 'edit'
    }
  },

  validations: {
    editedGroups: {
      required,
      minLength: 1
    },
    subject: {
      name: {
        required
      },
      key: {
        required
      },
      regex: {
        required
      }
    }
  },

  methods: {
    readSubject(subjectId: string) {
      return Subject.readSubject(subjectId, this.subject)
        .then(subject => {
          this.subject = { ...subject }
          this.resetGroupChanges()
          this.isOutdated = false
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('SUBJECT', 1) })
            })
          )
          console.error(err)
        })
    },

    createSubject() {
      if (!this.$v.$invalid) {
        return Subject.createSubject(this.subject)
          .then(res => {
            return Promise.all(
              this.editedGroups.map(group => Group.addAffiliate('subject', group.id, res.id))
            ).then(() => {
              this.resetGroupChanges()
              Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('SUBJECT', 1) }), {
                remove: 5000
              })
              this.$router.push({
                name: 'subject',
                params: { subjectId: res.id }
              })
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SUBJECT', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SUBJECT', 1) })
          })
        )
      }
    },

    updateSubject() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Subject.updateSubject(this.subject.id, this.subject)
            .then(res => {
              this.subject.version = res.version
              return this.saveGroups('subject', this.subject.id).then(() => {
                Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('SUBJECT', 1) }), {
                  remove: 5000
                })
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SUBJECT', 1) }),
                    thing: this.$i18n.tc('SUBJECT', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SUBJECT', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SUBJECT', 1) }),
              thing: this.$i18n.tc('SUBJECT', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SUBJECT', 1) })
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteSubject() {
      Flow.readFlowsForSubject(this.subject.id)
        .then(usedFlows => {
          if (usedFlows.length) {
            const constraintText = this.$i18n.t('USED_CONSTRAINT_EX', {
              action: this.$i18n
                .t('DELETE', {
                  thing: this.$i18n.tc('SUBJECT', 1)
                })
                .toLowerCase(),
              list: usedFlows.map((f, i) => ` ${f.name}${i < usedFlows.length ? '' : ', '}`)
            })
            Toast.warning(this.$i18n.t('USED_CONSTRAINT', { thing: this.subject.name }), {
              remove: 10000,
              text: constraintText
            })
            throw new Error(constraintText)
          } else {
            return Promise.all(
              this.savedGroups.map(g => Group.removeAffiliate('subject', g.id, this.subject.id))
            ).then(() => Subject.deleteSubject(this.subject.id))
          }
        })
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('SUBJECT', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'subjects'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('SUBJECT', 1) })
            })
          )
          console.error(err)
        })
    },
    editMode() {
      this.mode = 'edit'
      this.readSubject(this.subjectId)
    },

    createMode() {
      this.mode = 'create'
      this.subject = { ...subject }
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.subject = changedItem
      this.subjectDiff = fullDiff
    },
    onRevert(subject) {
      this.subject = subject
      this.subjectDiff = {}
    }
  },

  mounted() {
    if (this.subjectId) {
      this.editMode()
    } else {
      this.createMode()
    }
  },

  watch: {
    subjectId(newSubjectId) {
      this.$v.$reset()
      if (newSubjectId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
